/** Blend Builder API client — cookie auth, /api/pureh/blend. */
declare const process: { env: Record<string, string | undefined> };

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/pureh/blend`;

async function req<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(init.headers || {}) },
    ...init,
  });
  if (!res.ok) {
    let d = res.statusText;
    try { const j = await res.json(); d = j.detail ?? d; } catch { /* ignore */ }
    throw new Error(typeof d === "string" ? d : JSON.stringify(d));
  }
  return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
}

export type Target = {
  id: string; name: string; house: string; year: number; gender: "M" | "W" | "U";
  family: string; family_ar: string; dna: string; key_chem: string; signature: string;
  companion_suggestion?: string | null;
};
export type Booster = { material: string; pct: number; isSignature?: boolean; isOptimizer?: boolean };
export type Perf = { longevity: number; projection: number; sillage: number };
export type Recommendation = {
  target: Target;
  body: { oil: string; pct: number };
  direction: { oil: string; pct: number; source: string };
  boosters: Booster[];
  concentration: string;
  performance: { before: Perf; after: Perf; hours_before: string; hours_after: string };
  warnings: { text: string; fix: string }[];
  disclaimer: string;
};
export type House = { house: string; count: number; dna: string; targetIds: string[] };
export type FamilyRow = { key: string; ar: string; concentration: string };
export type BatchSnapshot = {
  bottles: number; sizeMl: number; conc: number; effConc: number;
  costPerBottle: number; batchCost: number; profit: number; margin: number;
  currency: string; priceGOil: number; priceMlAlc: number; packaging: number; sellPrice: number;
};
export type UserBlend = {
  id: string; name: string; base_target_id: string;
  code?: string; status?: string; warnings?: string[];
  qc_checklist?: { item: string; done: boolean }[];
  retail_price?: number | null;
  body: { oil: string; pct: number }; direction: { oil: string; pct: number };
  boosters: Booster[]; concentration: string | null; notes: string;
  batch?: BatchSnapshot | null;
  created_at: string; updated_at: string;
};

export const blendApi = {
  targets: (q: { search?: string; house?: string; gender?: string; family?: string }) => {
    const p = new URLSearchParams();
    Object.entries(q).forEach(([k, v]) => { if (v) p.set(k, v); });
    return req<Target[]>(`/targets?${p.toString()}`);
  },
  recommendation: (id: string) => req<Recommendation>(`/targets/${id}/recommendation`),
  houses: () => req<House[]>("/houses"),
  families: () => req<FamilyRow[]>("/families"),
  blends: () => req<UserBlend[]>("/blends"),
  saveBlend: (b: Record<string, unknown>) => req<UserBlend>("/blends", { method: "POST", body: JSON.stringify(b) }),
  updateBlend: (id: string, patch: Record<string, unknown>) =>
    req<UserBlend>(`/blends/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
  deleteBlend: (id: string) => req<{ deleted: string }>(`/blends/${id}`, { method: "DELETE" }),
};
